<template>
  <div class="file-uploader-input flex align-start_ gap10">
    <div v-if="viewAsImg" class="img-container">
      <img class="val-img" :title="valToShow?.name || valToShow?.title" :src="fileToShow" :alt="valToShow?.name || valToShow?.title || $t('clickToUploadFile')" @click="clickInput" @load="onImageLoad" @error="onImageError"/>
      <div v-if="imageLoading" class="image-loader-overlay">
        <MiniLoader/>
      </div>
    </div>
    <p class="p-like flex align-center" v-else-if="!fileToShow && !isLoading" @click="clickInput">{{$t('clickToUploadFile')}}</p>
    <p class="p-like flex align-center ltr text-end" v-else-if="isLoading" @click="clickInput">{{loadingMsg? loadingMsg : $t('loading') + '...'}}</p>
    <div v-else class="p-like flex align-center gap10">
      <a target="_blanc" :href="fileToShow" :title="valToShow.name || valToShow.title">{{valToShow.name || valToShow.title}}</a>
      <span v-if="fileStatus" class="file-status" :class="`status-${fileStatus}`">
        <MiniLoader v-if="fileStatus === 'inQ' || fileStatus === 'process'" class="status-loader"/>
        <span v-if="fileStatus === 'inQ' || fileStatus === 'process'">
          {{$t('encrypting')}}<span v-if="encryptionProgress !== null"> {{encryptionProgress}}%</span>
        </span>
        <span v-else-if="fileStatus === 'done'" class="status-success">✓ {{$t('ready')}}</span>
        <span v-else-if="fileStatus === 'failed'" class="status-error">✗ {{$t('encryptionFailed')}}</span>
      </span>
    </div>
    <div class="buttons-container flex row align-center justify-center gap10">
      <input type="file" ref="inputEl" hidden @change="uploadFile" :accept="accept"/>
      <template v-if="!isLoading">
        <button @click.prevent.stop="clickInput" class="btn big primary_">{{$t('chooseFile')}}</button>
        <button v-if="enableCloudSelection" @click.prevent.stop="openCloudBrowser" class="btn big secondary_">{{$t('selectFromCloud')}}</button>
        <button v-if="fileToShow" @click.prevent.stop="clear" :title="$t('clear')" class="btn clear underline width-content danger_ round_">
          <!-- <div v-html="svgs.x" class="svg-parrent"></div> -->
          {{$t('clear')}}
        </button>
      </template>
      <div v-else class="loader-wrapper">
        <MiniLoader/>
      </div>
    </div>
    
    <!-- Cloud File Browser Modal -->
    <div v-if="showCloudBrowser" class="cloud-browser-modal" @click.self="closeCloudBrowser">
      <CloudFileBrowser
        :organizationId="uploadFolderName"
        :parentData="parentData"
        :sectionName="sectionName"
        :fieldName="fieldName"
        @file-selected="onCloudFileSelected"
        @close="closeCloudBrowser"
      />
    </div>
  </div>
</template>

<script>
import { fixFileSrcToThumbnail, chunkUploadFileToServer, getFileStatus, resizeImage } from '../../services/file.service';
import { alertService } from '@/apps/common/modules/common/services/alert.service';
import MiniLoader from '../MiniLoader.vue';
import CloudFileBrowser from './CloudFileBrowser.vue';
import { Utils } from '../../services/util.service';
import { getSvgs } from '@/assets/images/svgs';
export default {
  components: { MiniLoader, CloudFileBrowser },
  name: 'FileUploader',
  props: {
    value: [Object, String], // { src, title },
    accept: {
      type: String,
      default: ''
    },
    viewAsImg: [Boolean],
    onlySrc: [Boolean],
    uploadFolderName: [String],
    parentData: [Object],
    onupload: [Function],
    rootItem: [Object],
    enableCloudSelection: {
      type: Boolean,
      default: false
    },
    sectionName: {
      type: String,
      default: ''
    },
    fieldName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isLoading: false,
      previewSrc: '',
      loadingMsg: '',
      imageLoading: false,
      showCloudBrowser: false,
      fileStatus: null, // 'inQ', 'process', 'done', 'failed'
      encryptionProgress: null, // 0-100
      statusPollInterval: null
    }
  },
  computed: {
    fileToShow() {
      const fileSrc = this.previewSrc || (this.valToShow? fixFileSrcToThumbnail(this.valToShow, this.rootItem) : '');
      if (this.viewAsImg && !fileSrc) {
        return require('@/apps/megaphonApp/assets/images/upload_placeholder.svg');
      }
      return fileSrc;
    },
    valToShow() {
      return this.onlySrc ? { src: this.value, title: Utils.cropText(this.value, 30) } : this.value;
    },
    svgs() {
      return getSvgs();
    }
  },
  watch: {
    previewSrc(newVal) {
      if (newVal && this.viewAsImg) {
        this.imageLoading = true
      }
    },
    value: {
      handler(newVal) {
        this.checkAndStartPolling()
        
        // Restore previewSrc from value if we don't have one yet (preserves preview after parent update)
        if (newVal && typeof newVal === 'object' && !this.previewSrc) {
          this.previewSrc = newVal.previewSrc || newVal.src;
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    // Check if we need to start polling when component mounts
    this.checkAndStartPolling()
  },
  beforeDestroy() {
    // Stop polling when component is destroyed
    this.stopStatusPolling()
  },
  methods: {
    fixFileSrcToThumbnail,
    onImageLoad() {
      this.imageLoading = false
    },
    onImageError() {
      this.imageLoading = false
    },
    clickInput() {
      const { inputEl } = this.$refs;
      inputEl.click();
    },
    getFileFromInput(inputEl) {
      const file = inputEl.files[0];
      return file;
    },
    /**
     * Read file signature (magic numbers) from file header to verify actual file type
     * This provides security by checking file content, not just extension
     */
    async readFileSignature(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const arr = new Uint8Array(e.target.result)
          const header = Array.from(arr.slice(0, 12))
            .map(b => b.toString(16).padStart(2, '0'))
            .join(' ')
          resolve({ header, bytes: arr })
        }
        reader.onerror = () => reject(new Error(this.$t('fileReadError')))
        // Read first 12 bytes to check file signature
        reader.readAsArrayBuffer(file.slice(0, 12))
      })
    },
    /**
     * Validate file signature matches expected file type
     * Returns the detected MIME type based on file content
     */
    async validateFileSignature(file, expectedExtensions) {
      try {
        const { header, bytes } = await this.readFileSignature(file)
        
        // File signature mappings (magic numbers)
        const signatures = {
          // PDF: starts with %PDF
          pdf: {
            signatures: ['25504446'], // %PDF in hex
            mimeType: 'application/pdf'
          },
          // DOC: Microsoft Office 97-2003 (OLE2 format)
          doc: {
            signatures: ['d0cf11e0a1b11ae1'], // OLE2 header
            mimeType: 'application/msword'
          },
          // DOCX: ZIP-based format (Office Open XML)
          docx: {
            signatures: ['504b0304'], // ZIP header (PK..)
            mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            // Need to check for specific file inside ZIP
            checkZipContent: true
          },
          // XLS: Microsoft Office 97-2003
          xls: {
            signatures: ['d0cf11e0a1b11ae1'],
            mimeType: 'application/vnd.ms-excel'
          },
          // XLSX: ZIP-based format
          xlsx: {
            signatures: ['504b0304'],
            mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            checkZipContent: true
          },
          // PPT: Microsoft Office 97-2003
          ppt: {
            signatures: ['d0cf11e0a1b11ae1'],
            mimeType: 'application/vnd.ms-powerpoint'
          },
          // PPTX: ZIP-based format
          pptx: {
            signatures: ['504b0304'],
            mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            checkZipContent: true
          },
          // Images
          jpg: {
            signatures: ['ffd8ff'],
            mimeType: 'image/jpeg'
          },
          png: {
            signatures: ['89504e47'],
            mimeType: 'image/png'
          },
          gif: {
            signatures: ['47494638'],
            mimeType: 'image/gif'
          },
          webp: {
            signatures: ['52494646'], // RIFF
            mimeType: 'image/webp'
          },
          // Videos
          mp4: {
            signatures: ['000000', '66747970'], // ftyp box
            mimeType: 'video/mp4',
            checkOffset: 4 // ftyp appears at offset 4
          }
        }
        
        const headerHex = Array.from(bytes.slice(0, 8))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('')
        
        // Check each expected extension
        for (const ext of expectedExtensions) {
          const sig = signatures[ext.toLowerCase()]
          if (!sig) continue
          
          // Check if header matches signature
          let matches = false
          for (const sigHex of sig.signatures) {
            if (headerHex.startsWith(sigHex.toLowerCase()) || 
                headerHex.includes(sigHex.toLowerCase())) {
              matches = true
              break
            }
          }
          
          // For Office Open XML (DOCX, XLSX, PPTX), verify it's actually an Office file
          // by checking if it contains the expected XML structure in the ZIP
          if (matches && sig.checkZipContent) {
            // Read more bytes to check ZIP structure (need to read ZIP central directory)
            const fullReader = new FileReader()
            return new Promise((resolve, reject) => {
              fullReader.onload = async (e) => {
                try {
                  const fullBytes = new Uint8Array(e.target.result)
                  // Office Open XML files are ZIP archives, read as text to find XML entries
                  const text = new TextDecoder('utf-8', { fatal: false })
                    .decode(fullBytes.slice(0, Math.min(5000, fullBytes.length)))
                  
                  // Check for Office document markers in ZIP structure
                  let isValidOfficeFile = false
                  if (ext === 'docx') {
                    // DOCX should contain [Content_Types].xml with word/ in it
                    isValidOfficeFile = text.includes('[Content_Types].xml') && 
                                      (text.includes('word/') || text.includes('application/vnd.openxmlformats'))
                  } else if (ext === 'xlsx') {
                    // XLSX should contain [Content_Types].xml with xl/ in it
                    isValidOfficeFile = text.includes('[Content_Types].xml') && 
                                      (text.includes('xl/') || text.includes('spreadsheetml'))
                  } else if (ext === 'pptx') {
                    // PPTX should contain [Content_Types].xml with ppt/ in it
                    isValidOfficeFile = text.includes('[Content_Types].xml') && 
                                      (text.includes('ppt/') || text.includes('presentationml'))
                  }
                  
                  if (isValidOfficeFile) {
                    resolve({ valid: true, mimeType: sig.mimeType, extension: ext })
                  } else {
                    // ZIP file but not Office document
                    reject(new Error(this.$t('fileIsZipNotOffice', [ext.toUpperCase()])))
                  }
                } catch (err) {
                  reject(new Error(this.$t('failedToValidateFile', [ext.toUpperCase(), err.message])))
                }
              }
              fullReader.onerror = () => reject(new Error(this.$t('failedToValidateFileSignature')))
              // Read first 5KB to check ZIP structure
              fullReader.readAsArrayBuffer(file.slice(0, Math.min(5000, file.size)))
            })
          }
          
          if (matches) {
            return { valid: true, mimeType: sig.mimeType, extension: ext }
          }
        }
        
        // No matching signature found
        return { valid: false, reason: this.$t('fileSignatureMismatch') }
      } catch (error) {
        return { valid: false, reason: error.message || this.$t('failedToValidateFileSignature') }
      }
    },
    validateImageFile(file) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve(true); // Valid image
        };
        
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error(this.$t('invalidImageFile')));
        };
        
        img.src = url;
      });
    },
    validateVideoFile(file) {
      return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        const url = URL.createObjectURL(file);
        
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(url);
          resolve(true); // Valid video
        };
        
        video.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error(this.$t('invalidVideoFile')));
        };
        
        video.src = url;
      });
    },
    async validateFileType(file) {
      // Get file extension
      const fileName = file.name.toLowerCase();
      const lastDotIdx = fileName.lastIndexOf('.');
      const extension = lastDotIdx !== -1 ? fileName.substring(lastDotIdx + 1) : '';
      
      // Get accept prop value
      const accept = (this.accept || '').toLowerCase();
      
      // Dangerous file extensions that should never be allowed
      const dangerousExtensions = ['exe', 'bat', 'cmd', 'com', 'pif', 'scr', 'vbs', 'js', 'jar', 'app', 'deb', 'pkg', 'dmg', 'msi'];
      if (dangerousExtensions.includes(extension)) {
        return Promise.reject(new Error(this.$t('fileTypeNotAllowed')));
      }
      
      // If accept is empty or '*', only block dangerous files (already done above)
      if (!accept || accept === '*') {
        return Promise.resolve(true);
      }
      
      // MIME type to extension mapping
      const mimeTypeToExtensions = {
        'application/pdf': ['pdf'],
        'application/msword': ['doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
        'application/vnd.ms-excel': ['xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
        'application/vnd.ms-powerpoint': ['ppt'],
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['pptx'],
        'text/plain': ['txt'],
        'text/csv': ['csv']
      }
      
      // Collect all accepted extensions from accept string
      const acceptedExtensions = new Set()
      
      // Split accept string by comma and process each part
      const acceptParts = accept.split(',').map(part => part.trim())
      
      for (const part of acceptParts) {
        if (!part) continue
        
        // Check if it's a MIME type (contains '/')
        if (part.includes('/')) {
          // Handle wildcard MIME types
          if (part === 'image/*') {
            const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']
            imageExtensions.forEach(ext => acceptedExtensions.add(ext))
          } else if (part === 'video/*') {
            const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'm4v']
            videoExtensions.forEach(ext => acceptedExtensions.add(ext))
          } else if (part === 'audio/*') {
            const audioExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac']
            audioExtensions.forEach(ext => acceptedExtensions.add(ext))
          } else if (part.startsWith('image/')) {
            // Specific image MIME type - validate as image
            const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']
            if (!imageExtensions.includes(extension)) {
              return Promise.reject(new Error(this.$t('pleaseSelectImageFile')))
            }
            // Validate it's actually an image by content
            return this.validateImageFile(file)
          } else if (part.startsWith('video/')) {
            // Specific video MIME type - validate as video
            const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'm4v']
            if (!videoExtensions.includes(extension)) {
              return Promise.reject(new Error(this.$t('pleaseSelectVideoFile')))
            }
            // Validate it's actually a video by content
            return this.validateVideoFile(file)
          } else {
            // Map MIME type to extension(s)
            const extensions = mimeTypeToExtensions[part]
            if (extensions && Array.isArray(extensions)) {
              extensions.forEach(ext => acceptedExtensions.add(ext))
            } else {
              // Try to extract extension from MIME type (e.g., "application/pdf" -> "pdf")
              const mimeParts = part.split('/')
              if (mimeParts.length === 2) {
                const subtype = mimeParts[1].toLowerCase()
                // For simple cases like application/pdf, the subtype is the extension
                if (subtype && !subtype.includes('*') && !subtype.includes('.') && subtype.length <= 5) {
                  acceptedExtensions.add(subtype)
                }
              }
            }
          }
        } else if (part.includes('.')) {
          // It's an extension (e.g., ".pdf" or "pdf")
          const ext = part.replace(/^\./, '').toLowerCase()
          if (ext && !ext.includes('/')) {
            acceptedExtensions.add(ext)
          }
        } else {
          // Bare extension without dot
          const ext = part.toLowerCase()
          if (ext && !ext.includes('/') && !ext.includes('.')) {
            acceptedExtensions.add(ext)
          }
        }
      }
      
      // If we have collected accepted extensions, validate against them
      if (acceptedExtensions.size > 0) {
        // Check extension matches
        if (!acceptedExtensions.has(extension)) {
          const extList = Array.from(acceptedExtensions).sort().join(', ')
          return Promise.reject(new Error(this.$t('pleaseSelectFileWithExtensions', [extList])))
        }
        
        // SECURITY: Validate file signature matches extension (verify actual file content)
        // This prevents malicious files with fake extensions (e.g., .exe renamed to .pdf)
        // Only validate signatures for document types that have detectable magic numbers
        const documentTypesWithSignatures = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'mp4']
        
        if (documentTypesWithSignatures.some(docType => acceptedExtensions.has(docType))) {
          const signatureResult = await this.validateFileSignature(file, Array.from(acceptedExtensions))
          
          if (!signatureResult.valid) {
            return Promise.reject(new Error(
              signatureResult.reason || 
              this.$t('fileContentMismatch')
            ))
          }
        }
        
        // For document types, we've validated the signature
        // For images/videos, additional validation is done above
        return Promise.resolve(true)
      }
      
      // Default: allow if not dangerous (but still validate signature for security)
      if (extension) {
        const signatureResult = await this.validateFileSignature(file, [extension])
        if (!signatureResult.valid && !signatureResult.reason.includes('ZIP')) {
          // Allow if signature check failed but it's not a critical security issue
          // (some file types may not have detectable signatures)
          console.warn('File signature validation warning:', signatureResult.reason)
        }
      }
      
      return Promise.resolve(true);
    },
    getImageDimensions(file) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        
        img.onload = () => {
          const dimensions = {
            width: img.naturalWidth,
            height: img.naturalHeight
          };
          URL.revokeObjectURL(url);
          resolve(dimensions);
        };
        
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load image'));
        };
        
        img.src = url;
      });
    },
    calculatePreviewSize(originalWidth, originalHeight, percentage = 0.1) {
      // Calculate new dimensions maintaining aspect ratio
      let newWidth = Math.round(originalWidth * percentage);
      let newHeight = Math.round(originalHeight * percentage);
      
      // Set minimum and maximum dimensions
      const minWidth = 150;
      const minHeight = 150;
      const maxWidth = 800;
      const maxHeight = 600;
      
      // If calculated size is too small, use minimum dimensions while maintaining aspect ratio
      // BUT only if the original image is larger than minimum (no upscaling)
      if ((newWidth < minWidth || newHeight < minHeight) && 
          (originalWidth >= minWidth || originalHeight >= minHeight)) {
        const aspectRatio = originalWidth / originalHeight;
        if (aspectRatio > 1) {
          // Landscape: width is larger
          newWidth = minWidth;
          newHeight = Math.round(minWidth / aspectRatio);
        } else {
          // Portrait or square: height is larger or equal
          newHeight = minHeight;
          newWidth = Math.round(minHeight * aspectRatio);
        }
      }
      
      // If calculated size is too large, use maximum dimensions while maintaining aspect ratio
      if (newWidth > maxWidth || newHeight > maxHeight) {
        const aspectRatio = originalWidth / originalHeight;
        if (aspectRatio > 1) {
          // Landscape: width is larger
          newWidth = maxWidth;
          newHeight = Math.round(maxWidth / aspectRatio);
        } else {
          // Portrait or square: height is larger or equal
          newHeight = maxHeight;
          newWidth = Math.round(maxHeight * aspectRatio);
        }
      }
      
      // If original image is smaller than minimum, keep original size (no upscaling)
      if (originalWidth < minWidth && originalHeight < minHeight) {
        newWidth = originalWidth;
        newHeight = originalHeight;
      }
      
      return { width: newWidth, height: newHeight };
    },
    async optimizePreviewSize(file, originalPreviewSrc) {
      try {
        const urlObj = new URL(originalPreviewSrc)
        if (urlObj.origin !== 'https://images.megaphon.co.il') return;
        
        const dimensions = await this.getImageDimensions(file)
        const optimizedSize = this.calculatePreviewSize(dimensions.width, dimensions.height, 0.1)
        
        const pathname = urlObj.pathname.startsWith('/uploads/') 
          ? urlObj.pathname 
          : `/uploads${urlObj.pathname}`
        
        const params = new URLSearchParams(urlObj.search)
        params.set('format', 'webp')
        params.set('width', optimizedSize.width)
        params.set('height', optimizedSize.height)
        
        this.previewSrc = `${urlObj.origin}${pathname}?${params.toString()}`
      } catch (error) {
        console.warn('Failed to optimize preview size:', error)
      }
    },
    async doUploadFile(file, uploadFolderName, parentData) {
      this.isLoading = true;
      this.loadingMsg = '';
      try {
        file = await resizeImage(file);
        let lastDotIdx = file.name.lastIndexOf('.');
        if (lastDotIdx === -1) lastDotIdx = file.name.length;
        const type = file.name.substring(lastDotIdx+1);
        const originalName = file.name;
        
        const uploadedRes  = await chunkUploadFileToServer(file, uploadFolderName, parentData, ({percents, msg}) => {
          if (msg === 'encrypting') this.loadingMsg = 'Done uploding, encrypting...';
          else this.loadingMsg = `${percents.toFixed(3)}%`;
        });
        
        const previewUrl = uploadedRes.previewSrc || uploadedRes.src;
        if (previewUrl) {
          this.previewSrc = previewUrl;
          if (this.viewAsImg) this.imageLoading = true;
          
          const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(type.toLowerCase());
          if (isImage) {
            this.optimizePreviewSize(file, previewUrl).catch(err => {
              console.warn('Preview optimization failed:', err);
            });
          }
        }
        
        this.loadingMsg = '';
        const newVal = { 
          name: originalName, 
          title: originalName, 
          type, 
          src: uploadedRes.src || uploadedRes.previewSrc, 
          fileId: uploadedRes.fileId,
          previewSrc: this.previewSrc
        };
        if (['mp4'].includes(type)) alertService.toast({type: 'safe', msg: this.$t(`videoUploadedAlert`), timeout: null});
        return newVal;
      } catch(err) {
        alertService.toast({type: 'danger', msg: `cantUploadFileError`});
        throw err;
      } finally {
        this.isLoading = false;
        this.loadingMsg = '';
      }
    },
    async uploadFile() {
      const file = this.getFileFromInput(this.$refs.inputEl);
      if (!file) return;
      
      // Validate file type before uploading
      try {
        await this.validateFileType(file);
      } catch (error) {
        alertService.toast({
          type: 'danger',
          msg: error.message || this.$t('invalidFileError') || 'Invalid file type'
        });
        // Clear the input so user can try again
        this.$refs.inputEl.value = '';
        return;
      }
      
      const newVal = await this.doUploadFile(file, this.uploadFolderName, this.parentData);
      if (this.onupload) this.onupload(this.onlySrc? newVal.src : newVal);
      this.$emit('input', this.onlySrc? newVal.src : newVal);
    },

    clear() {
      this.previewSrc = ''
      this.imageLoading = false
      this.fileStatus = null
      this.encryptionProgress = null
      this.stopStatusPolling()
      this.$emit('input', this.onlySrc? '' : {});
    },
    openCloudBrowser() {
      this.showCloudBrowser = true
    },
    closeCloudBrowser() {
      this.showCloudBrowser = false
    },
    onCloudFileSelected(file) {
      // Handle cloud file selection (same format as upload)
      this.previewSrc = file.src || ''
      const newVal = { 
        name: file.name, 
        title: file.name, 
        type: file.type, 
        src: file.src, 
        fileId: file.fileId,
        status: file.status
      }
      this.fileStatus = file.status || null
      this.encryptionProgress = file.encryptionProgress !== undefined ? file.encryptionProgress : null
      if (this.onupload) this.onupload(this.onlySrc? newVal.src : newVal)
      this.$emit('input', this.onlySrc? newVal.src : newVal)
      this.closeCloudBrowser()
      
      // Start polling if status is 'inQ' or 'process'
      if (file.fileId && (file.status === 'inQ' || file.status === 'process')) {
        this.startStatusPolling(file.fileId)
      }
    },
    async startStatusPolling(fileId) {
      // Stop any existing polling
      this.stopStatusPolling()
      
      // Poll every 3 seconds
      this.statusPollInterval = setInterval(async () => {
        try {
          const response = await getFileStatus(fileId)
          this.fileStatus = response.status
          this.encryptionProgress = response.encryptionProgress !== undefined ? response.encryptionProgress : null
          
          // Stop polling if encryption is done or failed
          if (response.status === 'done' || response.status === 'failed') {
            this.stopStatusPolling()
            // Clear progress when done
            if (response.status === 'done') {
              this.encryptionProgress = null
            }
            
            // Update the value with new status
            if (this.value && typeof this.value === 'object') {
              const updatedVal = { ...this.value, status: response.status }
              this.$emit('input', this.onlySrc? updatedVal.src : updatedVal)
            }
            
            // Show error toast if failed
            if (response.status === 'failed') {
              alertService.toast({
                type: 'danger',
                msg: response.error || this.$t('encryptionFailed') || 'Encryption failed'
              })
            }
          }
        } catch (err) {
          console.error('Error polling file status:', err)
          // Continue polling even on error (might be temporary network issue)
        }
      }, 3000) // Poll every 3 seconds
    },
    stopStatusPolling() {
      if (this.statusPollInterval) {
        clearInterval(this.statusPollInterval)
        this.statusPollInterval = null
      }
    },
    checkAndStartPolling() {
      // Check if we have a fileId and should start polling
      if (this.value && typeof this.value === 'object' && this.value.fileId) {
        const status = this.value.status || this.fileStatus
        if (status === 'inQ' || status === 'process') {
          this.fileStatus = status
          this.startStatusPolling(this.value.fileId)
        } else if (status) {
          this.fileStatus = status
        }
      } else {
        // No fileId or status is done/failed - stop polling
        this.stopStatusPolling()
        if (!this.value || (typeof this.value === 'object' && !this.value.fileId)) {
          this.fileStatus = null
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/global/index';

.file-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9em;
  
  .status-loader {
    width: 14px;
    height: 14px;
  }
  
  &.status-inQ,
  &.status-process {
    color: var(--clr-primary, #007bff);
  }
  
  &.status-done {
    color: var(--clr-success, #28a745);
    
    .status-success {
      font-weight: 500;
    }
  }
  
  &.status-failed {
    color: var(--clr-danger, #dc3545);
    
    .status-error {
      font-weight: 500;
    }
  }
}
.file-uploader-input {
  .img-container {
    position: relative;
    display: inline-block;
  }
  .val-img {
    width: em(160px);
    height: em(110px);
    object-fit: contain;
    border: em(1px) solid var(--clr-0);

    background-color: rgb(223, 223, 223);
  }
  .image-loader-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.8);
    z-index: 1;
  }
  .buttons-container {
    min-width: em(110px);
    flex-shrink: 0;
    flex-wrap: wrap;
  }
  .loader-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: em(80px);
    width: 100%;
    .mini-loader {
      width: em(50px);
      height: em(50px);
    }
  }
  button {
    min-width: em(110px);
  }
  .p-like {
    // width: em(200px);
    // width: em(160px);
    overflow: hidden;
    text-overflow: ellipsis;
  }
  @media (max-width: $small-screen-break) {
    // flex-direction: column;
    flex-wrap: wrap;
    gap: em(5px) !important;
  }
  
  .text-ph, .clear {
    height: 1em;
  }
}

.cloud-browser-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
</style>